import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Types ───

/** Catalog of system invoice print templates available in v1 (PRD.238 §6.1). */
export type InvoicePrintTemplateId =
  | 'classic_billing'
  | 'clean_classic'
  | 'compact_modern'
  | 'statement_invoice'
  | 'minimal_billing'
  // PRD.284 — Letterhead Classic (traditional pre-printed stationery).
  | 'classic_letterhead';

/** Which configured clinic logo to render on the invoice printout. */
export type InvoicePrintLogoSource = 'standard' | 'horizontal' | 'none';

/** PowerSync sqlite row shape — booleans are stored as 0/1 integers. */
export interface InvoicePrintSettingsRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  template_id: string | null;
  logo_source: string | null;
  clinic_name: string | null;
  clinic_address: string | null;
  clinic_phone: string | null;
  clinic_email: string | null;
  clinic_website: string | null;
  tax_id: string | null;
  footer_text: string | null;
  show_patient_demographics: number | null;
  show_patient_number: number | null;
  show_invoice_number: number | null;
  show_invoice_date: number | null;
  show_visit_date: number | null;
  show_provider: number | null;
  show_item_quantity: number | null;
  show_item_unit_price: number | null;
  show_item_discount: number | null;
  show_item_tax: number | null;
  show_item_amount: number | null;
  show_subtotal: number | null;
  show_discount_total: number | null;
  show_tax_total: number | null;
  show_payments: number | null;
  show_balance_due: number | null;
  show_footer_notes: number | null;
  show_signature_block: number | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** UI-friendly settings shape used by the wizard. */
export interface InvoicePrintSettingsUI {
  id: string;
  templateId: InvoicePrintTemplateId;

  logoSource: InvoicePrintLogoSource;
  clinicName: string;
  clinicAddress: string;
  clinicPhone: string;
  clinicEmail: string;
  clinicWebsite: string;
  taxId: string;
  footerText: string;

  showPatientDemographics: boolean;
  showPatientNumber: boolean;
  showInvoiceNumber: boolean;
  showInvoiceDate: boolean;
  showVisitDate: boolean;
  showProvider: boolean;

  showItemQuantity: boolean;
  showItemUnitPrice: boolean;
  showItemDiscount: boolean;
  showItemTax: boolean;
  showItemAmount: boolean;

  showSubtotal: boolean;
  showDiscountTotal: boolean;
  showTaxTotal: boolean;
  showPayments: boolean;
  showBalanceDue: boolean;

  showFooterNotes: boolean;
  showSignatureBlock: boolean;
}

export const INVOICE_PRINT_DEFAULTS: Omit<InvoicePrintSettingsUI, 'id'> = {
  templateId: 'classic_billing',
  logoSource: 'standard',
  clinicName: '',
  clinicAddress: '',
  clinicPhone: '',
  clinicEmail: '',
  clinicWebsite: '',
  taxId: '',
  footerText: '',

  showPatientDemographics: true,
  showPatientNumber: false,
  showInvoiceNumber: true,
  showInvoiceDate: true,
  showVisitDate: false,
  showProvider: true,

  showItemQuantity: true,
  showItemUnitPrice: true,
  showItemDiscount: true,
  showItemTax: false,
  showItemAmount: true,

  showSubtotal: true,
  showDiscountTotal: true,
  showTaxTotal: true,
  showPayments: true,
  showBalanceDue: true,

  showFooterNotes: true,
  showSignatureBlock: false,
};

// ─── Queries ───

/** Fetch the org-default Invoice Print Settings row (clinic_id IS NULL). */
export function qInvoicePrintSettings(): QuerySpec<[]> {
  return {
    key: ['invoice-print-settings'] as const,
    sql: `SELECT * FROM invoice_print_settings WHERE clinic_id IS NULL ORDER BY updated_at DESC LIMIT 1`,
    params: [],
    watch: ['invoice_print_settings'],
  };
}

// ─── Mappers ───

function asBool(v: number | null, fallback: boolean): boolean {
  if (v == null) return fallback;
  return v === 1;
}

export function mapInvoicePrintSettingsToUI(row: InvoicePrintSettingsRow): InvoicePrintSettingsUI {
  const d = INVOICE_PRINT_DEFAULTS;
  return {
    id: row.id,
    templateId: (row.template_id ?? d.templateId) as InvoicePrintTemplateId,
    logoSource: (row.logo_source ?? d.logoSource) as InvoicePrintLogoSource,

    clinicName: row.clinic_name ?? '',
    clinicAddress: row.clinic_address ?? '',
    clinicPhone: row.clinic_phone ?? '',
    clinicEmail: row.clinic_email ?? '',
    clinicWebsite: row.clinic_website ?? '',
    taxId: row.tax_id ?? '',
    footerText: row.footer_text ?? '',

    showPatientDemographics: asBool(row.show_patient_demographics, d.showPatientDemographics),
    showPatientNumber: asBool(row.show_patient_number, d.showPatientNumber),
    showInvoiceNumber: asBool(row.show_invoice_number, d.showInvoiceNumber),
    showInvoiceDate: asBool(row.show_invoice_date, d.showInvoiceDate),
    showVisitDate: asBool(row.show_visit_date, d.showVisitDate),
    showProvider: asBool(row.show_provider, d.showProvider),

    showItemQuantity: asBool(row.show_item_quantity, d.showItemQuantity),
    showItemUnitPrice: asBool(row.show_item_unit_price, d.showItemUnitPrice),
    showItemDiscount: asBool(row.show_item_discount, d.showItemDiscount),
    showItemTax: asBool(row.show_item_tax, d.showItemTax),
    showItemAmount: asBool(row.show_item_amount, d.showItemAmount),

    showSubtotal: asBool(row.show_subtotal, d.showSubtotal),
    showDiscountTotal: asBool(row.show_discount_total, d.showDiscountTotal),
    showTaxTotal: asBool(row.show_tax_total, d.showTaxTotal),
    showPayments: asBool(row.show_payments, d.showPayments),
    showBalanceDue: asBool(row.show_balance_due, d.showBalanceDue),

    showFooterNotes: asBool(row.show_footer_notes, d.showFooterNotes),
    showSignatureBlock: asBool(row.show_signature_block, d.showSignatureBlock),
  };
}

export function mapUIToInvoicePrintSettingsColumns(ui: Omit<InvoicePrintSettingsUI, 'id'>) {
  const b = (v: boolean) => (v ? 1 : 0);
  return {
    template_id: ui.templateId,
    logo_source: ui.logoSource,
    clinic_name: ui.clinicName || null,
    clinic_address: ui.clinicAddress || null,
    clinic_phone: ui.clinicPhone || null,
    clinic_email: ui.clinicEmail || null,
    clinic_website: ui.clinicWebsite || null,
    tax_id: ui.taxId || null,
    footer_text: ui.footerText || null,

    show_patient_demographics: b(ui.showPatientDemographics),
    show_patient_number: b(ui.showPatientNumber),
    show_invoice_number: b(ui.showInvoiceNumber),
    show_invoice_date: b(ui.showInvoiceDate),
    show_visit_date: b(ui.showVisitDate),
    show_provider: b(ui.showProvider),

    show_item_quantity: b(ui.showItemQuantity),
    show_item_unit_price: b(ui.showItemUnitPrice),
    show_item_discount: b(ui.showItemDiscount),
    show_item_tax: b(ui.showItemTax),
    show_item_amount: b(ui.showItemAmount),

    show_subtotal: b(ui.showSubtotal),
    show_discount_total: b(ui.showDiscountTotal),
    show_tax_total: b(ui.showTaxTotal),
    show_payments: b(ui.showPayments),
    show_balance_due: b(ui.showBalanceDue),

    show_footer_notes: b(ui.showFooterNotes),
    show_signature_block: b(ui.showSignatureBlock),

    updated_at: new Date().toISOString(),
  };
}
